import * as THREE from "three";
import type { GLTF } from "three/examples/jsm/loaders/GLTFLoader.js";
import { extractCliffEdges } from "@tribez/shared";
import type { AssetBundle, NatureKey } from "../assets";
import { toToon, bakeBaseAO } from "../toon";

// ─── Density constants (easy to tune) ───────────────────────────────────────
// Grid step for mushroom scatter (lower = denser). At step=6, ~(256/6)²≈1820
// candidate cells, each placed with MUSH_PROB probability.
const MUSH_STEP   = 6;    // meters between mushroom grid cells
const MUSH_PROB   = 0.12; // ~12% of candidates → ~220 total mushrooms

// Grid step for fern scatter. Ferns are moderate ground cover.
const FERN_STEP   = 8;    // meters between fern grid cells
const FERN_PROB   = 0.18; // ~18% of candidates → ~230 total ferns

// Bone/carcass/tusk are very rare — prehistoric forest-floor finds only.
const BONE_STEP   = 20;   // large grid for bones = wide spacing = rare
const BONE_PROB   = 0.06; // ~6% of candidates → ~12 total bone clusters

// Keep a margin near map edges and skip the dead center (spawn zones).
const EDGE_MARGIN  = 8;    // units from map boundary to skip
const CENTER_SKIP  = 18;   // radius around map center (128,128) to skip

// Scale ranges per asset category. Assets sit at near-native scale; baked_set.json
// bbox confirms mushrooms 0.2–1.2m, ferns 1.2–1.3m, bones/carcasses 0.2–2.3m.
const MUSH_SCALE_MIN  = 0.75;   // applied on top of native-scale (target ~0.3–0.6m)
const MUSH_SCALE_MAX  = 1.30;
const FERN_SCALE_MIN  = 0.85;   // ferns read well at near-native
const FERN_SCALE_MAX  = 1.15;
const BONE_SCALE_MIN  = 0.80;   // keep bones small, don't upscale carcasses
const BONE_SCALE_MAX  = 1.05;

// Spatial culling chunk size (world units). Matches doodads.ts.
const CHUNK_SIZE = 64;
// ─────────────────────────────────────────────────────────────────────────────

// Deterministic [0, 1) hash — same formula as world-grass.ts so positions are
// stable across reloads without Math.random.
function hash01(x: number, z: number, salt: number): number {
  const v = Math.sin(x * 12.9898 + z * 78.233 + salt * 31.4159) * 43758.5453;
  return v - Math.floor(v);
}

// Pick a random integer in [0, n) deterministically from a hash value.
function hashPick(x: number, z: number, salt: number, n: number): number {
  return Math.floor(hash01(x, z, salt) * n);
}

type Renderable = { geom: THREE.BufferGeometry; mat: THREE.Material };

function extractRenderables(gltf: GLTF): Renderable[] {
  const out: Renderable[] = [];
  gltf.scene.traverse((node) => {
    const m = node as THREE.Mesh;
    if (!m.isMesh) return;
    const mat = Array.isArray(m.material) ? m.material[0]! : m.material;
    bakeBaseAO(m.geometry);
    // No occlusion cutout: mushrooms/ferns/bones are all sub-hero-height ground
    // cover (≤~2.4m), so they never hide the hero and stippling them is just
    // distracting (playtest 2026-06-25). See toon.ts CUTOUT_MIN_HEIGHT.
    out.push({ geom: m.geometry, mat: toToon(mat, { noiseScale: 2.5 }) });
  });
  return out;
}

// Emit one InstancedMesh per geometry primitive per spatial chunk, matching
// the doodads.ts emitChunkedInstances pattern for per-cell frustum culling.
function emitChunkedInstances(
  root: THREE.Object3D,
  geom: THREE.BufferGeometry,
  mat: THREE.Material,
  matrices: THREE.Matrix4[],
  name: string,
): void {
  const cells = new Map<string, THREE.Matrix4[]>();
  for (const m of matrices) {
    const cx = Math.floor(m.elements[12] / CHUNK_SIZE);
    const cz = Math.floor(m.elements[14] / CHUNK_SIZE);
    const key = `${cx},${cz}`;
    let arr = cells.get(key);
    if (!arr) { arr = []; cells.set(key, arr); }
    arr.push(m);
  }
  let ci = 0;
  for (const arr of cells.values()) {
    const inst = new THREE.InstancedMesh(geom, mat, arr.length);
    inst.name = `${name}#${ci++}`;
    inst.castShadow = false;   // small ground detail — same rule as doodadPlant
    inst.receiveShadow = false;
    arr.forEach((mx, i) => inst.setMatrixAt(i, mx));
    inst.instanceMatrix.needsUpdate = true;
    inst.computeBoundingSphere(); // per-cell frustum cull
    root.add(inst);
  }
}

type ScatterGroup = {
  keys: NatureKey[];
  step: number;
  prob: number;
  scaleMin: number;
  scaleMax: number;
  salt: number; // base salt offset so each group's hash stream is independent
};

export function buildScatterDetail(
  scene: THREE.Scene,
  assets: AssetBundle,
  sampleHeight: (x: number, z: number) => number,
  mapUnits: number,
): void {
  const root = new THREE.Group();
  root.name = "scatter-detail";

  const groups: ScatterGroup[] = [
    {
      keys:     ["exMush1A", "exMush2A", "exMush3A", "exMush4A", "exMush5A"],
      step:     MUSH_STEP,
      prob:     MUSH_PROB,
      scaleMin: MUSH_SCALE_MIN,
      scaleMax: MUSH_SCALE_MAX,
      salt:     100,
    },
    {
      keys:     ["exFern1A", "exFern2A"],
      step:     FERN_STEP,
      prob:     FERN_PROB,
      scaleMin: FERN_SCALE_MIN,
      scaleMax: FERN_SCALE_MAX,
      salt:     200,
    },
    {
      keys:     ["exBone1A", "exBone2A", "exCarcass1A", "exCarcass2A", "exTusk1A"],
      step:     BONE_STEP,
      prob:     BONE_PROB,
      scaleMin: BONE_SCALE_MIN,
      scaleMax: BONE_SCALE_MAX,
      salt:     300,
    },
  ];

  const centerX = mapUnits * 0.5;
  const centerZ = mapUnits * 0.5;
  const centerSkip2 = CENTER_SKIP * CENTER_SKIP;
  const dummy = new THREE.Object3D();

  for (const grp of groups) {
    // Pre-extract renderables for each key in this group.
    const renderablesByKey: Renderable[][] = grp.keys.map((k) =>
      extractRenderables(assets.nature[k]),
    );

    // Collect matrices per key index.
    const matricesByKey: THREE.Matrix4[][] = grp.keys.map(() => []);

    for (let gz = EDGE_MARGIN; gz < mapUnits - EDGE_MARGIN; gz += grp.step) {
      for (let gx = EDGE_MARGIN; gx < mapUnits - EDGE_MARGIN; gx += grp.step) {
        // Probability gate — deterministic.
        if (hash01(gx, gz, grp.salt) > grp.prob) continue;

        // Per-cell jitter so the grid doesn't show through.
        const jx = (hash01(gx, gz, grp.salt + 1) - 0.5) * grp.step * 0.8;
        const jz = (hash01(gx, gz, grp.salt + 2) - 0.5) * grp.step * 0.8;
        const x = gx + grp.step * 0.5 + jx;
        const z = gz + grp.step * 0.5 + jz;

        // Skip near map edge (post-jitter) and map center.
        if (x < EDGE_MARGIN || x > mapUnits - EDGE_MARGIN) continue;
        if (z < EDGE_MARGIN || z > mapUnits - EDGE_MARGIN) continue;
        const dx = x - centerX;
        const dz = z - centerZ;
        if (dx * dx + dz * dz < centerSkip2) continue;

        // Pick a variant deterministically.
        const keyIdx = hashPick(gx, gz, grp.salt + 3, grp.keys.length);
        const scale =
          grp.scaleMin +
          hash01(gx, gz, grp.salt + 4) * (grp.scaleMax - grp.scaleMin);
        const rot = hash01(gx, gz, grp.salt + 5) * Math.PI * 2;
        const y = sampleHeight(x, z);

        dummy.position.set(x, y, z);
        dummy.rotation.set(0, rot, 0);
        dummy.scale.setScalar(scale);
        dummy.updateMatrix();
        matricesByKey[keyIdx]!.push(dummy.matrix.clone());
      }
    }

    // Emit chunked InstancedMeshes for each key.
    for (let ki = 0; ki < grp.keys.length; ki++) {
      const matrices = matricesByKey[ki]!;
      if (matrices.length === 0) continue;
      const renderables = renderablesByKey[ki]!;
      renderables.forEach((r, ri) => {
        emitChunkedInstances(
          root,
          r.geom,
          r.mat,
          matrices,
          `scatter:${grp.keys[ki]}:${ri}`,
        );
      });
    }
  }

  scene.add(root);
}

// ─── Cliff-lip vegetation (Bet 3 slice ②) ───────────────────────────────────
// KTS drapes ferns/leafy bushes over the TOP edge of every steep rock face so
// hills read as organic overgrowth, not extruded terraces. We reproduce that by
// scattering the same toon foliage along the cliff LIP — the grid line where a
// plateau meets the drop — anchored on the plateau's TOP surface so fronds
// overhang the exposed rock (slice ① textures the face; this softens its edge).
//
// CLIENT-ONLY decoration: no collision, no navmesh, no doodad-resolver — the lips
// come straight from the shared cliff-edge extractor (the same source the server
// QA overlay uses), so placement is deterministic and needs no new data.

// Which foliage drapes the lips. Ferns are the KTS staple; the common bush adds
// occasional leafy mass. All flow through toToon() so they match the world style.
const LIP_VEG_KEYS: NatureKey[] = ["exFern1A", "exFern2A", "exFern1A", "bushCommon"];

// Per-lip-segment (1m of edge) probability of seeding a clump. Kept low so the
// lip reads as clumped-with-gaps overgrowth, NOT a continuous painted hedge.
const LIP_PROB = 0.20;
// Each seeded segment drops between MIN and MAX plants (a little clump).
const LIP_CLUMP_MIN = 1;
const LIP_CLUMP_MAX = 3;
// How far (m) onto the HIGH plateau the clump base sits, past the exaggerated
// step-ramp, so sampleHeight lands on flat plateau top and the plant grounds
// there while its foliage overhangs the lip rather than floating in the void.
const LIP_INSET = 1.25;
// Jitter (m) of clump members: along the lip (tangent) spreads the clump, a small
// inward push keeps every member grounded on the plateau, never over the face.
const LIP_TANGENT_SPREAD = 1.4;
const LIP_INWARD_JITTER  = 0.9;
// Scale range — lip ferns read a touch larger than ground scatter so the drape
// is visible from the MOBA camera.
const LIP_SCALE_MIN = 0.90;
const LIP_SCALE_MAX = 1.45;

/**
 * Scatter fern/bush clumps along cliff lips so steep faces read organic.
 * Reuses the scatter-detail instancing/toon pipeline; adds a group named
 * "cliff-lip-veg" (toggle its .visible for before/after QA).
 */
export function buildCliffLipVegetation(
  scene: THREE.Scene,
  assets: AssetBundle,
  sampleHeight: (x: number, z: number) => number,
  cliffLevels: Uint16Array,
  heights: Float32Array,
): void {
  const root = new THREE.Group();
  root.name = "cliff-lip-veg";

  // Lip segments: grid-space endpoints + which side is the high plateau (via the
  // outward normal nx,nz that points high→low). Height-field only classifies the
  // walls; the actual Y comes from sampleHeight so we sit on the visual surface.
  const edges = extractCliffEdges(cliffLevels, heights);

  const renderablesByKey: Renderable[][] = LIP_VEG_KEYS.map((k) =>
    extractRenderables(assets.nature[k]),
  );
  const matricesByKey: THREE.Matrix4[][] = LIP_VEG_KEYS.map(() => []);
  const dummy = new THREE.Object3D();
  let plantCount = 0;

  for (const e of edges) {
    // Midpoint of the lip segment (grid coords == world metres, 1 cell = 1m).
    const mx = (e.ax + e.bx) * 0.5;
    const mz = (e.az + e.bz) * 0.5;

    // Gate this segment deterministically → clumps with gaps, not a rim outline.
    if (hash01(mx, mz, 700) > LIP_PROB) continue;

    // Inward = onto the HIGH plateau (opposite the high→low outward normal).
    // Tangent = along the lip. Both are unit grid steps from the extractor.
    const inx = -e.nx, inz = -e.nz;
    const tlen = Math.hypot(e.bx - e.ax, e.bz - e.az) || 1;
    const tx = (e.bx - e.ax) / tlen, tz = (e.bz - e.az) / tlen;

    const count =
      LIP_CLUMP_MIN +
      hashPick(mx, mz, 701, LIP_CLUMP_MAX - LIP_CLUMP_MIN + 1);

    for (let i = 0; i < count; i++) {
      const s = 710 + i * 6; // independent hash stream per clump member
      const along  = (hash01(mx, mz, s + 0) - 0.5) * LIP_TANGENT_SPREAD;
      const inward = LIP_INSET + hash01(mx, mz, s + 1) * LIP_INWARD_JITTER;
      const x = mx + tx * along + inx * inward;
      const z = mz + tz * along + inz * inward;
      const y = sampleHeight(x, z);

      const keyIdx = hashPick(mx, mz, s + 2, LIP_VEG_KEYS.length);
      const scale =
        LIP_SCALE_MIN + hash01(mx, mz, s + 3) * (LIP_SCALE_MAX - LIP_SCALE_MIN);
      const rot = hash01(mx, mz, s + 4) * Math.PI * 2;

      dummy.position.set(x, y, z);
      dummy.rotation.set(0, rot, 0);
      dummy.scale.setScalar(scale);
      dummy.updateMatrix();
      matricesByKey[keyIdx]!.push(dummy.matrix.clone());
      plantCount++;
    }
  }

  for (let ki = 0; ki < LIP_VEG_KEYS.length; ki++) {
    const matrices = matricesByKey[ki]!;
    if (matrices.length === 0) continue;
    renderablesByKey[ki]!.forEach((r, ri) => {
      emitChunkedInstances(root, r.geom, r.mat, matrices, `lipveg:${LIP_VEG_KEYS[ki]}:${ri}`);
    });
  }

  scene.add(root);
  console.log(
    `[cliff-lip-veg] ${plantCount} plants across ${edges.length} lip segments`,
  );
}
